import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";

const LINKS = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/testimonials", label: "Stories" },
  { to: "/faqs", label: "FAQs" },
  { to: "/referrals", label: "Referrals" },
  { to: "/careers", label: "Careers" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "border-b border-sand/80 bg-linen/85 backdrop-blur-xl shadow-[0_8px_30px_rgba(28,56,41,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <span className="block h-11 w-11 drop-shadow-[0_6px_14px_rgba(200,109,81,0.45)] transition-transform duration-500 group-hover:rotate-12 group-hover:scale-105">
            <LogoMark className="h-11 w-11" />
          </span>
          <span className="leading-none">
            <span className="block font-serif text-xl font-semibold tracking-tight text-forest">Brightside</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.28em] text-sage">Befriending · Glasgow & Ayr</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 xl:flex" data-testid="nav-desktop-links">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-300 hover:text-terracotta ${isActive ? "text-terracotta" : "text-forest/80"}`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <Button
            asChild
            data-testid="nav-book-consultation-button"
            className="rounded-full bg-forest px-5 text-sm text-linen shadow-[0_10px_24px_rgba(28,56,41,0.25)] transition-all duration-300 hover:bg-terracotta hover:shadow-[0_10px_24px_rgba(200,109,81,0.35)]"
          >
            <Link to="/contact">Book a free consultation</Link>
          </Button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-sand bg-white/70 text-forest xl:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="border-b border-sand bg-linen/95 backdrop-blur-xl xl:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 font-serif text-2xl tracking-tight transition-colors ${
                        isActive ? "bg-sage-tint text-forest" : "text-forest/80 hover:bg-linen-deep"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <Button
                asChild
                data-testid="nav-mobile-book-button"
                className="mt-4 rounded-full bg-terracotta py-6 text-base text-linen hover:bg-terracotta-deep"
              >
                <Link to="/contact" onClick={() => setOpen(false)}>Book a free consultation</Link>
              </Button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
